import { AppDateTime } from '@macs-frontend/core/utils/datetime';
import { AppTime } from '@macs-frontend/core/utils/time';
import { UUID } from '@macs-frontend/core/utils/uuid';
import {
  MdAction,
  Omit,
  ServiceTypes,
  Task,
  UploadedFile,
} from '@macs-frontend/features/shared';
import { AbstractLocation } from '@macs-frontend/web-macs/shared/models/abstract-location';
import { MdBaseAction } from '@macs-frontend/web-macs/shared/models/action';
import { BaseActionPlanningDateAction } from '@macs-frontend/web-macs/shared/models/action-planning';
import { BaseEntity } from '@macs-frontend/web-macs/shared/models/base-entity.model';
import { BookingPerson } from '@macs-frontend/web-macs/shared/models/booking-person';
import { BaseExternalUser } from '@macs-frontend/web-macs/shared/models/external-user';
import { BaseProject, Project } from '@macs-frontend/web-macs/shared/models/project';
import { Ratesheet } from '@macs-frontend/web-macs/shared/models/ratesheet.model';
import { TaskTypes } from '@macs-frontend/features/shared/enums/task-types.enum';
import { ActionTeamProject } from '@macs-frontend/features/shared/models/action-team';
import { InvoiceItem } from '@macs-frontend/features/shared/models/invoice-item.model';
import { box, Boxed } from 'ngrx-forms';
import { ActionsFormEntity } from '@macs-frontend/features/actions-form-shared/models/actions-form.model';

export enum TeamCostViaCompanyStatus {
  ACTIVE = 'ACTIVE',
  ARCHIVED = 'ARCHIVED',
}

export interface TeamCostViaCompany extends BaseEntity {
  name: string;
  address: string;
  status: TeamCostViaCompanyStatus;
  isDefault: boolean;
}

export interface DictionaryService {
  billablePrice: number;
  description: string | null;
  uuid: UUID;
  name: string;
  unitPrice: number;
}

export interface BaseTCService {
  uuid: UUID;
  name: string;
  description: string | null;
  qty: number;
  unitPrice: number;
  billablePrice: number;
  billablePriceMargin: number;
  amount: number;
  type: ServiceTypes;
}

export interface TCService extends BaseTCService {
  dictionaryService?: DictionaryService;
}

export interface BaseTCTask {
  uuid: UUID;
  name: string;
  description: string;
  qty: number;
  unitPrice: number;
  billablePrice: number;
  billablePriceMargin: number;
  amount: number;
  type: TaskTypes;
}

export interface TCTask extends BaseTCTask {
  task?: Task;
}

export interface DictionaryTransport {
  code: string;
  uuid: UUID;
  name: string;
  variableCost?: number;
  costUnitPrice: number;
  costBillablePrice: number;
  distanceUnitPrice: number;
  distanceBillablePrice: number;
  handlingUnitPrice: number;
  handlingBillablePrice: number;
}

export interface DictionaryMaterial {
  uuid: UUID;
  name: string;
  unitPrice: number;
  billablePrice: number;
  type: string;
}

export interface SampleUnit {
  uuid: UUID;
  name: string;
  inTray: number;
  inLayer: number;
  inPalet: number;
  isActive: boolean;
}

export interface BaseMaterialGroupItem {
  uuid: UUID;
  name: string;
  qty: number;
  estimated: number;
  transported: number;
  inTray?: number;
  inLayer?: number;
  inPalet?: number;
  inChecklist: boolean;
  iceBoxes?: number;
  iceCubes?: number;
  layersOnPallet?: number;
}

export interface BaseProjectMaterialGroupItem {
  uuid: UUID;
  name: string;
  qtyRequired: number;
  qtyLimit: number;
  totalQtyLimit?: number;
  reserveUnit?: number;
  reservePercent?: number;
  qtyTotal?: number;
  totalEstimated?: number;
  totalTransported?: number;
  unitPrice: number;
  billablePrice: number;
  retourPrice: number;
  totalBillablePrice: number;
  totalUnitPrice: number;
  createdAt?: AppDateTime;
  isHiddenForBudgetOverview?: boolean;
}

export interface BaseProjectMaterialGroup {
  uuid: UUID;
  type: MaterialGroupTypes;
  name: string;
}

export interface BaseATTransport {
  uuid: UUID;
  distance: number;
  name: string;
  distanceUnitPrice: number;
  distanceBillablePrice: number;
  totalUnitPrice: number;
  totalBillablePrice: number;
  totalDistanceUnitPrice: number;
  totalDistanceBillablePrice: number;
  costUnitPrice: number;
  costBillablePrice: number;
  totalCostUnitPrice: number;
  totalCostBillablePrice: number;
  handlingUnitPrice: number;
  handlingBillablePrice: number;
  code: string;
  qty: number;
  licensePlate?: string;
  parentUuid?: UUID;
  isParentTransport: boolean;
}

export interface TCTransport extends BaseATTransport {
  dictionaryTransport?: DictionaryTransport;
  driver?: BaseExternalUser;
  group?: string | null;
  billableTotalAmount?: number;
}

export interface ReadOnlyConstraints {
  messages: string[];
  bookedRoles: BookedRole[];
  childActions: BookedAction[];
  bookedTransports: BookedTransport[];
  hasConstraints: boolean;
}

export interface BookedRole {
  uuid: string;
  name: string;
  bookedCount: number;
  roleKey?: string;
}

export interface BookedAction {
  uuid: string;
  name: string;
  bookedCount: number;
  actionKey?: string;
}

export interface BookedTransport {
  uuid: UUID;
  name: string;
  licensePlate: string;
  parentUuid?: UUID;
}

export interface BaseTeamCost extends BaseEntity {
  name: string;
  description: string;
  logo: string;
  normalHours: AppTime;
  hasCustomActions?: boolean;
  breakHours: AppTime;
  substractBreakHoursForHR: boolean;
  substractBreakHoursForClient: boolean;
  applyHolidayRates: boolean;
  contactCount: number;
  compositionReservePercentage: number;
  extraCost?: number;
  billablePrice: number;
  margin: number;
  unitPrice: number;
  ccBillable?: number;
  ccMargin?: number;
  ccReal?: number;
  targetContacts?: number;
  totalContactsVal?: number;
  totalContactsPercent?: number;
  budgetVal?: number;
  budgetPercent?: number;
  actions?: number;
  totalCost?: number;
  totalActions?: number;
  realActionsQty?: number;
  realTeamCost?: number;
  isCustom?: boolean;
  customNumber?: number;
  compositionRolesTotalBillablePrice?: number;
  compositionRolesTotalUnitPrice?: number;
  servicesTotalBillablePrice?: number;
  servicesTotalUnitPrice?: number;
  tasksTotalBillablePrice?: number;
  tasksTotalUnitPrice?: number;
  transportsTotalBillablePrice?: number;
  transportsTotalUnitPrice?: number;
  materialsTotalUnitPrice?: number;
  materialsTotalBillablePrice?: number;
  materials?: import('../materials/models/teamcost-materials.model').TeamcostMaterialRow[];
  startDate?: AppDateTime;
  endDate?: AppDateTime;
  startDateUTC?: AppDateTime;
  endDateUTC?: AppDateTime;
  viaCompanyStartDate?: AppDateTime;
  viaCompanyEndDate?: AppDateTime;
  viaCompanyStartDateUTC?: AppDateTime;
  viaCompanyEndDateUTC?: AppDateTime;
  directToEventStartDate?: AppDateTime;
  directToEventEndDate?: AppDateTime;
  directToEventStartDateUTC?: AppDateTime;
  directToEventEndDateUTC?: AppDateTime;
  actionUuid: UUID | null;
  projectedSamples: number | null;
  projectedContacts: number | null;
}

export enum ScheduleAssignmentTagCode {
  PICK_UP = 'pick_up',
  TRAVEL_TO_EVENT = 'travel_to_event',
  BUILD = 'build',
  START_EVENT = 'start_event',
  CLEAN_UP = 'clean_up',
  TRAVEL_TO_COMPANY = 'travel_to_company',
  UNLOADING_AND_DEBRIEFING_COMPANY = 'uploading_and_debriefing_company',
}

export interface ScheduleAssignmentTag {
  uuid: UUID;
  name: string;
  code: ScheduleAssignmentTagCode | null;
  color: string;
  position: number;
}

export interface BaseScheduleAssignment {
  uuid: UUID;
  name: string;
  startTime: number;
  endTime: number;
  duration: number;
  type: ScheduleAssignmentTypes;
  color: string;
  startDay: number;
  endDay: number;
  tag?: ScheduleAssignmentTag | null;
}

export enum ScheduleStartLocationTypes {
  ViaCompany = 'VIA_COMPANY',
  DirectToEvent = 'DIRECT_TO_EVENT',
  AtCompany = 'AT_COMPANY',
}

export interface BaseScheduleStartLocation {
  uuid: UUID;
  type: ScheduleStartLocationTypes;
  name: string;
  address: string;
  isDefault: boolean;
  viaCompany?: TeamCostViaCompany;
}

export interface ScheduleAssignment extends BaseScheduleAssignment {
  scheduleStartLocations: BaseScheduleStartLocation[];
  startLocationsAsString: string;
}

export interface TCScheduleAssignmentForServer {
  uuid: UUID;
  name: string;
  startTime: number;
  endTime: number;
  duration: number;
  type: ScheduleAssignmentTypes;
  color: string;
  scheduleStartLocations: UUID[];
  position: number;
  tag?: UUID | null;
}

export interface ScheduleStartLocation extends BaseScheduleStartLocation {
  scheduleAssignments?: ScheduleAssignment[];
}

export enum ScheduleAssignmentTypes {
  PreAction = 'PRE_ACTION',
  OnEvent = 'ON_EVENT',
  AfterAction = 'AFTER_ACTION',
}

export interface BaseTCCompositionRole {
  uuid: UUID;
  costPrice: number;
  sellPrice: number;
  defaultCostPricePerHour: number;
  defaultSellPricePerHour: number;
  costPricePerHour: number;
  sellPricePerHour: number;
  overtimeCostPricePerHour: number;
  overtimeSellPricePerHour: number;
  transportCostPricePerDistanceUnit: number;
  transportSellPricePerDistanceUnit: number;
  transportDistance: number;
  quantity: number;
  fullness: number;
  pendingBookingPersonCount?: number;
  bookedBookingPersonCount?: number;
  amount: number;
  roleName: string;
  normalRatePercentage: number;
  overtimeRatePercentage: number;
  holidayHourPricePercentage: number;
  holidayHourPriceUnitPrice: number;
  holidayHourPriceBillablePrice: number;
  holidayOvertimePricePercentage: number;
  holidayOvertimePriceUnitPrice: number;
  holidayOvertimePriceBillablePrice: number;
  ownCarViaUnitPrice: number;
  ownCarViaBillablePrice: number;
  ownCarDirectUnitPrice: number;
  ownCarDirectBillablePrice: number;
}

export interface CompositionRoleTeamCostAction extends MdBaseAction {
  actionPlanningDate: BaseActionPlanningDateAction;
  actionLocation?: AbstractLocation;
}

export interface CompositionRoleTeamCost
  extends BaseTeamCost,
    Pick<TeamCost, 'scheduleStartLocations'> {
  action?: CompositionRoleTeamCostAction;
  materialFiles?: UploadedFile[];
}

export interface TCCompositionRole extends BaseTCCompositionRole {
  bookedBookingPersons?: BookingPerson[];
  pendingBookingPersons?: BookingPerson[];
  bookingPersons?: BookingPerson[];
  teamCost?: CompositionRoleTeamCost;
  teamCostRole?: TeamCostRole;
  scheduleStartLocation: null | ScheduleStartLocation;
  billableHours?: string;
}

export interface MaterialGroupItem extends BaseMaterialGroupItem {
  dictionaryMaterial?: DictionaryMaterial;
  dictionarySampleUnit?: SampleUnit;
  projectMaterialGroupItem?: Omit<ProjectMaterialGroupItem, 'materialGroupItems'>;
  projectMaterialGroup?: BaseProjectMaterialGroup;
  teamCost?: BaseTeamCost;
  // Carried over from the materials-ms row when the action detail page builds
  // its rows; mirrors the same fields on the action-team MaterialGroupItem.
  pictureUrl?: string | null;
  // ST044: the material's one-time "material list remark".
  listRemarks?: string | null;
}

export interface ProjectMaterialGroupItem extends BaseProjectMaterialGroupItem {
  projectMaterialGroup: BaseProjectMaterialGroup;
  dictionaryMaterial?: DictionaryMaterial;
  dictionarySampleUnit?: SampleUnit;
  materialGroupItems?: MaterialGroupItem[];
}

export interface ProjectMaterialGroup extends BaseProjectMaterialGroup {
  projectMaterialGroupItems: ProjectMaterialGroupItem[];
}

export interface TeamCostProject
  extends BaseProject,
    Pick<
      Project,
      'projectMaterialGroups' | 'projectGroup' | 'briefing' | 'client' | 'company'
    > {}

export interface BaseMaterialGroup {
  uuid: UUID;
  name: string;
  type: MaterialGroupTypes;
}

export interface MaterialGroup extends BaseMaterialGroup {
  materialGroupItems: MaterialGroupItem[];
}

export interface TeamCost extends BaseTeamCost {
  invoiceUuid: UUID;
  // ST054/ST055: general material list remarks (teamcost-level, read-only on the action).
  materialListRemarks?: string | null;
  action: MdAction;
  materialGroups: MaterialGroup[];
  materialGroupItems: MaterialGroupItem[];
  services: TCService[];
  tasks: TCTask[];
  transports: TCTransport[];
  scheduleStartLocations: ScheduleStartLocation[];
  scheduleAssignments: ScheduleAssignment[];
  compositionRoles: TCCompositionRole[];
  parent?: BaseTeamCost;
  ratesheet?: Ratesheet;
  availableViaCompanies: TeamCostViaCompany[];
  project?: ActionTeamProject | null;
  materialFiles: UploadedFile[];
  invoiceItem?: InvoiceItem | null;
  readOnlyConstraints: ReadOnlyConstraints;
  actionsCount: number;
  debriefing: ActionsFormEntity;
  companyForm: ActionsFormEntity;
  clientForm: ActionsFormEntity;
}

export interface TeamCostView extends TeamCost {
  generalServices?: TCService[];
  generalServicesTotal?: number;
  peopleServices?: TCService[];
  peopleServicesTotal?: number;
  generalTasks?: TCTask[];
  generalTasksTotal?: number;
  peopleTasks?: TCTask[];
  peopleTasksTotal?: number;
  transportsTotal?: number;
}

export enum SaveTeamCostKind {
  One = 0,
  ExceptBooked = 1,
  All = 3,
}

export interface BaseTeamCostSummaryItem {
  title: string;
  cost: number;
  total: number;
  extraClass: string | null;
  isTotal: boolean;
}

export enum TeamCostTabs {
  GeneralInfo = 'general',
  TeamSchedule = 'schedule',
  TeamSetup = 'setup',
  Materials = 'materials',
  Services = 'services',
  Tasks = 'tasks',
  Warehouse = 'warehouse',
  Transport = 'transport',
  Overview = 'overview',
  Metrics = 'metrics',
}

export enum CalcRoleByTypes {
  HourBillablePrice = 'HourBillablePrice',
  DistanceBillablePrice = 'DistanceBillablePrice',
}

export enum MaterialGroupTypes {
  Disposable = 'DISPOSABLE',
  Recurring = 'RECURRING',
  SampleUnit = 'SAMPLE',
  Clothes = 'CLOTHES',
}

export enum MaterialsSaveStatuses {
  NotSaved = 1,
  Saving,
  Saved,
}

export interface TCCreateRoleForm
  extends Omit<TCCompositionRole, 'scheduleStartLocation' | 'teamCostRole'> {
  scheduleStartLocation: null | UUID;
  billableHours: string;
  teamCostRole: Boxed<TeamCostRole | null | undefined>;
}

export interface StatisticsTime {
  hours: number;
  minutes: number;
  hoursAsFloat: number;
}

export interface ScheduleTimeStats {
  total: StatisticsTime;
  onEvent: StatisticsTime;
  preAfterAction: StatisticsTime;
}

export interface TeamCostLevel {
  uuid: UUID;
  //
  status: TeamCostLevelStatus;
  //
  name: string;
  costPrice: number;
  regularRate: number;
  studentRate: number;
  flexRate: number;
  freelanceRate: number;
}

export enum TeamCostLevelStatus {
  ACTIVE = 'ACTIVE',
  ARCHIVED = 'ARCHIVED',
}

export interface TeamCostRole {
  uuid: UUID;
  //
  status: TeamCostRoleStatus;
  //
  name: string;
  color: string;
  regularJc: TeamCostJointCommittee | null;
  studentJc: TeamCostJointCommittee | null;
  flexJc: TeamCostJointCommittee | null;
  freelanceJc: TeamCostJointCommittee | null;
  //
  teamCostLevel: TeamCostLevel | null;
  teamCostJointCommittees: TeamCostJointCommittee[];
  //
  createdAt?: AppDateTime;
  updatedAt?: AppDateTime;
}

export type TeamCostRoleForServer = Omit<
  TeamCostRole,
  | 'teamCostLevel'
  | 'regularJc'
  | 'studentJc'
  | 'flexJc'
  | 'freelanceJc'
  | 'teamCostJointCommittees'
  | 'color'
> & {
  teamCostLevel: UUID | null;
  regularJc: UUID | null;
  studentJc: UUID | null;
  flexJc: UUID | null;
  freelanceJc: UUID | null;
  color: string | null;
};

export enum TeamCostRoleStatus {
  ACTIVE = 'ACTIVE',
  ARCHIVED = 'ARCHIVED',
}

export interface TeamCostJointCommittee {
  uuid: UUID;
  //
  status: TeamCostJointCommitteeStatus;
  //
  name: string;
  code: string | null;
  isRegular: boolean;
  isStudent: boolean;
  isFlex: boolean;
  isFreelance: boolean;
}

export enum TeamCostJointCommitteeStatus {
  ACTIVE = 'ACTIVE',
  ARCHIVED = 'ARCHIVED',
}

export interface TeamCostTeamType {
  uuid: UUID;
  //
  status: TeamCostTeamTypeStatus;
  //
  name: string;
  isCreateContract: boolean;
  //
  createdAt?: AppDateTime;
  updatedAt?: AppDateTime;
}

export enum TeamCostTeamTypeStatus {
  ACTIVE = 'ACTIVE',
  ARCHIVED = 'ARCHIVED',
}
